class SecondScreenNoteScrollService {
    constructor(
        private noteContainerAttribute: string,
        private noteIdAttribute: string,
        private encountersListContainerClass: string,
        private noteListIdAttribute: string,
        private noteAutoScrollMargin: number
    ) {
        document.addEventListener("scroll", () => this.handleScroll());
    }

    private onNoteChangeCallBacks: ((noteId: string) => void)[] = [];
    private currentNoteId = "";
    private lastScrollValue = 0;
    private lastScrollRelativeValue = 0;
    private isManagedScroll = false;

    public addOnNoteChangeCallback(onNoteChange: (noteId: string) => void): void {
        this.onNoteChangeCallBacks.push(onNoteChange);
    }

    public setOnNoteChangeCallback(onNoteChange: (noteId: string) => void): void {
        this.onNoteChangeCallBacks = [onNoteChange];
    }

    public scrollToWindowTop(): void {
        this.isManagedScroll = true;
        window.scroll({ top: 0 });
    }

    public scrollToNote(noteId: string, relativeScrollValue: number = 0): void {
        const noteOffsetTop = this.getNoteOffsetTop(noteId);
        this.isManagedScroll = true;
        window.scroll({
            top: noteOffsetTop + relativeScrollValue
        });
    }

    private getNoteOffsetTop(noteId: string): number {
        const noteIdSelector = `${this.noteContainerAttribute}[${this.noteIdAttribute}='${noteId}']`;
        const patientBannerHeight = this.getPatientBannerHeight();
        return document.querySelector<HTMLElement>(noteIdSelector)?.parentElement?.offsetTop - patientBannerHeight;
    }

    private handleScroll(): void {
        if (Math.abs(this.lastScrollValue - document.documentElement.scrollTop) > 200) {
            if (!this.isManagedScroll) {
                this.scrollToNote(this.currentNoteId, this.lastScrollRelativeValue);
                return;
            }
        }

        if (this.currentNoteId && !this.isManagedScroll) {
            this.lastScrollRelativeValue = document.documentElement.scrollTop - this.getNoteOffsetTop(this.currentNoteId);
        }

        this.isManagedScroll = false;
        this.lastScrollValue = document.documentElement.scrollTop;

        const noteContainers = Array.from<HTMLElement>(document.querySelectorAll(this.noteContainerAttribute));
        const currentContainer: HTMLElement = this.getCurrentNoteContainer(noteContainers);
        const newNoteId = currentContainer?.attributes?.[this.noteIdAttribute]?.value ?? "";
        if (newNoteId !== this.currentNoteId) {
            this.onNoteChangeCallBacks?.forEach(callBack => callBack(newNoteId));
            this.currentNoteId = newNoteId;
            this.lastScrollRelativeValue = document.documentElement.scrollTop - this.getNoteOffsetTop(this.currentNoteId);
            this.scrollListToSelectedEncounter();
        }
    }

    private getCurrentNoteContainer(noteContainers: HTMLElement[]): HTMLElement {
        const visibleContainers = noteContainers?.filter(this.isNoteContainerVisible);
        let currentContainer: HTMLElement = null;
        if (visibleContainers?.any()) {
            if (visibleContainers.length === 1) {
                if (visibleContainers[0].offsetTop - document.documentElement.scrollTop - this.noteAutoScrollMargin <= document.documentElement.clientHeight / 2) {
                    currentContainer = visibleContainers[0];
                }
            }
            else if (visibleContainers.length >= 2) {
                if (visibleContainers[1].offsetTop - document.documentElement.scrollTop - this.noteAutoScrollMargin <= document.documentElement.clientHeight / 2) {
                    currentContainer = visibleContainers[1];
                }
                else {
                    currentContainer = visibleContainers[0];
                }
            }
        }
        return currentContainer;
    }

    private isNoteContainerVisible(noteContainer: HTMLElement): boolean {
        const bounds = noteContainer.getBoundingClientRect();
        return (noteContainer.offsetTop >= document.documentElement.scrollTop && noteContainer.offsetTop <= document.documentElement.scrollTop + document.documentElement.clientHeight) ||
            (noteContainer.offsetTop + bounds.height >= document.documentElement.scrollTop && noteContainer.offsetTop + bounds.height <= document.documentElement.scrollTop + document.documentElement.clientHeight) ||
            (noteContainer.offsetTop <= document.documentElement.scrollTop && noteContainer.offsetTop + bounds.height >= document.documentElement.scrollTop + document.documentElement.clientHeight);
    }

    private scrollListToSelectedEncounter(): void {
        const selectedEncounter =
            document.querySelector<HTMLElement>(`${this.encountersListContainerClass} [${this.noteListIdAttribute}='${this.currentNoteId}']`);

        if (selectedEncounter && !this.isSelectedEncounterVisibleInList(selectedEncounter))
            selectedEncounter.scrollIntoView();
    }

    private isSelectedEncounterVisibleInList(selectedEncounter: HTMLElement): boolean {
        if (!selectedEncounter)
            return false;

        const selectedEncounterRectParentTop = selectedEncounter.parentElement.parentElement.getBoundingClientRect().top;
        const selectedEncounterRect = selectedEncounter.getBoundingClientRect();
        return (
            selectedEncounterRect.top >= selectedEncounterRectParentTop &&
            selectedEncounterRect.left >= 0 &&
            selectedEncounterRect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
            selectedEncounterRect.right <= (window.innerWidth || document.documentElement.clientWidth)
        );
    }

    private getPatientBannerHeight(): number {
        const secondScreenPageContainer = document.getElementById("secondScreenPage");
        const bannerHeightPropertyName = "--patient-banner-height";
        const patientBannerHeight = secondScreenPageContainer?.style?.getPropertyValue(bannerHeightPropertyName) ?? "0";
        const parsedHeight = parseInt(patientBannerHeight);

        return (isNaN(parsedHeight) || IsNullOrUndefined(parsedHeight)) ? 0 : parsedHeight;
    }
}

export { SecondScreenNoteScrollService };
